import type { ReactNode } from 'react'
import type { NodeInfo } from '../../api'
import type { RelayRole } from './api'
import { bps } from './format'
import { ROLE_HINT, ROLE_LABEL, RoleGlyph } from './ui'

/*
 * Small pieces of Network › Nodes and the node card (proto pages/Nodes.tsx,
 * ui/kit.tsx: RoleGlyphs, Meter, Kpi). Numbers in mono with tabular figures,
 * fixed widths: the ~5 s updates do not move the layout.
 */

/** Roles as shapes (▷ ingress, ◇ relay, ▷| egress, ⬡ hub), named for screen readers. */
export function RoleGlyphs({ roles, labels, className = '', testId }: { roles?: readonly RelayRole[]; labels?: boolean; className?: string; testId?: string }) {
  if (!roles?.length) return <span className="text-faint">—</span>
  return (
    <span className={`inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-text-soft ${className}`} aria-label={`Roles: ${roles.map((r) => ROLE_LABEL[r]).join(', ')}`} data-testid={testId} data-roles={roles.join(',')}>
      {roles.map((r) => (
        <span key={r} className="inline-flex items-center gap-1 text-xs" title={ROLE_HINT[r]}>
          <RoleGlyph role={r} />
          {labels ? ROLE_LABEL[r] : <span className="sr-only">{ROLE_LABEL[r]}</span>}
        </span>
      ))}
    </span>
  )
}

/** A thin horizontal meter; the number next to it carries the value, the bar only repeats it. */
export function Meter({ pct, width = 40, warnAt = 85 }: { pct: number; width?: number; warnAt?: number }) {
  const v = Math.max(0, Math.min(100, pct))
  return (
    <span className="relative inline-block h-1.5 flex-none overflow-hidden rounded-full bg-line" style={{ width }} aria-hidden>
      <span className={`absolute inset-y-0 left-0 ${v >= warnAt ? 'bg-warn' : 'bg-text-soft'}`} style={{ width: `${v}%` }} />
    </span>
  )
}

/** Mb/s with one decimal under 10, whole above: fits a narrow column. */
const mb = (v: number) => {
  const x = v / 1e6
  return x >= 10 ? Math.round(x).toString() : x.toFixed(1)
}

/** List cell: CPU (meter + %), memory, traffic. */
export function LoadCell({ n }: { n: NodeInfo }) {
  const l = n.load
  if (n.status !== 'online') return <span className="text-faint">—</span>
  if (!l) return <span className="text-[11px] text-muted" title="The node sends no host stats (older software, no host/1)">no report</span>
  return (
    <span className="flex min-w-0 flex-col gap-0.5" data-testid={`node-load-${n.id}`}>
      <span className="flex items-center gap-1.5 whitespace-nowrap" title={`CPU ${Math.round(l.cpu_pct)}% of ${l.cpus} cores · memory ${Math.round(l.mem_pct)}%`}>
        <Meter pct={l.cpu_pct} width={28} />
        <span className={`num w-8 text-right font-mono text-xs ${l.cpu_pct >= 85 ? 'text-warn' : ''}`}>{Math.round(l.cpu_pct)}%</span>
      </span>
      <span className="num truncate font-mono text-[11px] text-muted" title="Memory used">
        mem {Math.round(l.mem_pct)}%
      </span>
      <span className="num truncate font-mono text-[11px] text-muted" title={`in ${bps(l.rx_bps)} · out ${bps(l.tx_bps)}`}>
        {l.rx_bps || l.tx_bps ? `↓${mb(l.rx_bps)} ↑${mb(l.tx_bps)} Mb/s` : 'idle'}
      </span>
    </span>
  )
}

/** Key figure of the node card's "Now" row. */
export function Kpi({ label, value, sub, tone, testId }: { label: string; value: ReactNode; sub?: ReactNode; tone?: 'warn' | 'fail'; testId?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5" data-testid={testId}>
      <span className="mono-label uppercase">{label}</span>
      <span className={`num font-mono text-[17px] leading-6 ${tone === 'warn' ? 'text-warn' : tone === 'fail' ? 'text-fail' : 'text-text'}`}>{value}</span>
      {sub && <span className="truncate text-[11px] text-muted">{sub}</span>}
    </div>
  )
}
